import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Archive, Edit2, Eye, Loader2, Plus, Save, Search, Star, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  slug: '',
  title: '',
  location: '',
  country: '',
  shortQuote: '',
  excerpt: '',
  content: '',
  imageUrl: '',
  imageAlt: '',
  featured: true,
  displayOrder: 999,
  status: 'draft',
  seoTitle: '',
  seoDescription: '',
};

const slugify = (value) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const inputClass = 'w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm';
const labelClass = 'block text-sm font-medium text-[#1A2E6E] mb-1';

export const AdminTestimonials = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadTestimonials = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (featuredFilter) params.set('featured', featuredFilter);
      params.set('page_size', '500');
      const res = await axios.get(`${API_URL}/api/admin/testimonials?${params.toString()}`);
      setItems(res.data.data ?? res.data ?? []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTestimonials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, featuredFilter]);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999)),
    [items]
  );

  const openCreate = () => {
    setEditingId(null);
    setSlugEdited(false);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setSlugEdited(true);
    setForm({ ...emptyForm, ...item });
    setShowForm(true);
  };

  const updateField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'name' && !slugEdited) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const validate = () => {
    for (const field of ['name', 'slug', 'shortQuote', 'excerpt', 'content']) {
      if (!String(form[field] || '').trim()) {
        toast.error(`${field} is required`);
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        displayOrder: Number(form.displayOrder) || 999,
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/testimonials/${editingId}`, payload);
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API_URL}/api/admin/testimonials`, payload);
        toast.success('Testimonial created');
      }
      setShowForm(false);
      await loadTestimonials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (item) => {
    if (!window.confirm(`Archive ${item.name}?`)) return;
    try {
      await axios.patch(`${API_URL}/api/admin/testimonials/${item.id}/archive`);
      toast.success('Testimonial archived');
      await loadTestimonials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive testimonial');
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/testimonials/${item.id}`);
      toast.success('Testimonial deleted');
      await loadTestimonials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete testimonial');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Testimonials</h1>
          <p className="text-[#1A2E6E]/60 mt-1">Create, publish, feature, order, and archive student stories.</p>
        </div>
        <button onClick={openCreate} className="btn-primary inline-flex items-center gap-2 justify-center">
          <Plus size={18} /> New Testimonial
        </button>
      </div>

      <div className="bg-white border border-[#E8EAF0] rounded-xl p-4 grid gap-3 md:grid-cols-[1fr_180px_180px_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40" size={16} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search testimonials..." className={`${inputClass} pl-10`} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputClass}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <select value={featuredFilter} onChange={(e) => setFeaturedFilter(e.target.value)} className={inputClass}>
          <option value="">Featured: all</option>
          <option value="true">Featured</option>
          <option value="false">Not featured</option>
        </select>
        <button onClick={loadTestimonials} className="px-5 py-3 rounded-xl bg-[#1A2E6E] text-white font-medium">Apply</button>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <Loader2 className="w-7 h-7 animate-spin text-[#D72B2B]" />
        </div>
      ) : (
        <div className="bg-white border border-[#E8EAF0] rounded-xl overflow-hidden">
          <div className="hidden md:grid grid-cols-[72px_1fr_120px_100px_90px_170px] gap-4 px-5 py-3 bg-[#F8F9FC] text-xs font-semibold uppercase text-[#1A2E6E]/50">
            <span>Image</span><span>Name</span><span>Status</span><span>Featured</span><span>Order</span><span>Actions</span>
          </div>
          {sortedItems.map((item) => (
            <div key={item.id} className="grid md:grid-cols-[72px_1fr_120px_100px_90px_170px] gap-4 px-5 py-4 border-t border-[#E8EAF0] items-center">
              <img src={item.imageUrl || '/logo.png'} alt={item.imageAlt || item.name} className="w-14 h-14 rounded-lg object-contain bg-[#F8F9FC]" />
              <div className="min-w-0">
                <p className="font-semibold text-[#1A2E6E] truncate">{item.name}</p>
                <p className="text-sm text-[#1A2E6E]/55 truncate">{item.location || item.title}</p>
                <p className="text-xs text-[#1A2E6E]/35">/testimonials/{item.slug}</p>
              </div>
              <span className="text-sm capitalize text-[#1A2E6E]">{item.status}</span>
              <span className="text-sm text-[#1A2E6E]">{item.featured ? 'Yes' : 'No'}</span>
              <span className="text-sm text-[#1A2E6E]">{item.displayOrder}</span>
              <div className="flex items-center gap-2">
                {item.status === 'published' && (
                  <a href={`/testimonials/${item.slug}`} target="_blank" rel="noreferrer" className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E]" title="View">
                    <Eye size={16} />
                  </a>
                )}
                <button onClick={() => openEdit(item)} className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E]" title="Edit"><Edit2 size={16} /></button>
                <button onClick={() => archive(item)} className="p-2 text-[#F5A623] hover:text-[#C57F00]" title="Archive"><Archive size={16} /></button>
                <button onClick={() => remove(item)} className="p-2 text-red-500 hover:text-red-700" title="Delete"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {sortedItems.length === 0 && <p className="p-8 text-center text-[#1A2E6E]/50">No testimonials found.</p>}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-[#09122F]/55 z-50 p-4 overflow-y-auto">
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl my-6">
            <div className="px-6 py-4 border-b border-[#E8EAF0] flex items-center justify-between">
              <div>
                <h2 className="font-sora text-xl font-bold text-[#1A2E6E]">{editingId ? 'Edit Testimonial' : 'New Testimonial'}</h2>
                <p className="text-sm text-[#1A2E6E]/50">Preview URL: /testimonials/{form.slug || 'slug'}</p>
              </div>
              <button onClick={() => setShowForm(false)} className="p-2 text-[#1A2E6E]/50 hover:text-[#1A2E6E]"><X size={20} /></button>
            </div>

            <div className="grid lg:grid-cols-[1fr_320px] gap-6 p-6">
              <div className="space-y-6">
                <section className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Name</label>
                    <input className={inputClass} value={form.name} onChange={(e) => updateField('name', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Slug</label>
                    <input className={inputClass} value={form.slug} onChange={(e) => { setSlugEdited(true); updateField('slug', slugify(e.target.value)); }} />
                  </div>
                  <div>
                    <label className={labelClass}>Title / Role</label>
                    <input className={inputClass} value={form.title} onChange={(e) => updateField('title', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Location</label>
                    <input className={inputClass} value={form.location} onChange={(e) => updateField('location', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Country</label>
                    <input className={inputClass} value={form.country} onChange={(e) => updateField('country', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Display Order</label>
                    <input type="number" className={inputClass} value={form.displayOrder} onChange={(e) => updateField('displayOrder', e.target.value)} />
                  </div>
                </section>

                <section className="space-y-4">
                  <div>
                    <label className={labelClass}>Short Quote <span className="text-[#1A2E6E]/40">({form.shortQuote.length} chars)</span></label>
                    <textarea rows={2} className={inputClass} value={form.shortQuote} onChange={(e) => updateField('shortQuote', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Excerpt <span className="text-[#1A2E6E]/40">({form.excerpt.length} chars)</span></label>
                    <textarea rows={4} className={inputClass} value={form.excerpt} onChange={(e) => updateField('excerpt', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Full Content</label>
                    <textarea rows={10} className={inputClass} value={form.content} onChange={(e) => updateField('content', e.target.value)} />
                  </div>
                </section>

                <section className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Image URL</label>
                    <input className={inputClass} value={form.imageUrl} onChange={(e) => updateField('imageUrl', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Image Alt</label>
                    <input className={inputClass} value={form.imageAlt} onChange={(e) => updateField('imageAlt', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>SEO Title</label>
                    <input className={inputClass} value={form.seoTitle} onChange={(e) => updateField('seoTitle', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>SEO Description</label>
                    <input className={inputClass} value={form.seoDescription} onChange={(e) => updateField('seoDescription', e.target.value)} />
                  </div>
                </section>

                <section className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-[#1A2E6E]">
                    <input type="checkbox" checked={form.featured} onChange={(e) => updateField('featured', e.target.checked)} />
                    Featured on Home
                  </label>
                  <select className="px-4 py-2 rounded-xl border border-[#D1D5DB] text-sm" value={form.status} onChange={(e) => updateField('status', e.target.value)}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </section>
              </div>

              <aside className="space-y-4">
                <div className="border border-[#E8EAF0] rounded-xl p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#1A2E6E]/45 mb-3">Image Preview</p>
                  {form.imageUrl ? (
                    <img src={form.imageUrl} alt={form.imageAlt || form.name} className="w-full h-44 object-contain bg-[#F8F9FC] rounded-xl" />
                  ) : (
                    <div className="h-44 rounded-xl bg-[#F8F9FC] flex items-center justify-center text-[#1A2E6E]/35">No image</div>
                  )}
                </div>
                <div className="border border-[#E8EAF0] rounded-xl p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#1A2E6E]/45 mb-3">Card Preview</p>
                  <div className="rounded-xl bg-[#F8F9FC] p-4">
                    <Star size={20} className="text-[#F5A623] fill-[#F5A623] mb-3" />
                    <p className="font-semibold text-[#1A2E6E] leading-snug">{form.shortQuote || 'Short quote'}</p>
                    <p className="text-sm text-[#1A2E6E]/65 mt-3 line-clamp-5">{form.excerpt || 'Excerpt preview...'}</p>
                    <p className="text-sm font-semibold text-[#1A2E6E] mt-4">{form.name || 'Student name'}</p>
                  </div>
                </div>
              </aside>
            </div>

            <div className="px-6 py-4 border-t border-[#E8EAF0] flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-xl border border-[#D1D5DB] text-[#1A2E6E]">Cancel</button>
              <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-xl bg-[#1A2E6E] text-white font-medium inline-flex items-center gap-2 disabled:opacity-50">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Testimonial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
